import type { AxiosInstance } from "axios";

import type { Courier, CourierOffice } from "@/types/courier";

import { useMutation, useQuery } from "@tanstack/react-query";

import { usePrivateAxios } from "./use-private-axios";

export interface CourierOfficeQuery {
	data: {
		data: CourierOffice[];
	};
	meta: {
		total: number;
		totalCount: number;
		totalPages: number;
		currentPage: number;
		itemsPerPage: number;
	};
}
export interface CouriersQueryResponse {
	data: {
		items: CourierOffice[];
	};
	meta: {
		total: number;
		totalCount: number;
		totalPages: number;
		currentPage: number;
		itemsPerPage: number;
	};
}

interface CourierQueryResponse {
	data: Courier;
}
interface CourierSearchParams {
	search?: string;
	courierId?: string;
	countryId?: string;
	stateId?: string;
	cityId?: string;
	page?: number;
	limit?: number;
}

async function fetchCouriers(axios: AxiosInstance) {
	const data = await axios.get<CouriersQueryResponse>("/currier/list_currier");

	return data.data;
}
async function fetchCourier(axios: AxiosInstance, courierId: string) {
	const data = await axios.get<CourierQueryResponse>(`/currier/view_currier/${courierId}`);
	return data.data.data;
}
async function fetchCourierOffices(axios: AxiosInstance, params?: CourierSearchParams) {
	const data = await axios.get<CourierOfficeQuery>(`/currier/list_courier_office`, {
		params
	});

	return data.data;
}
export function useCouriers() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["courier"],
		queryFn: () => fetchCouriers(privateAxios)
	});
}
export function useCourierOffices(params?: CourierSearchParams) {
	const privateAxios = usePrivateAxios();

	return useQuery({
		queryKey: ["courier-offices", params?.countryId, params?.stateId, params?.cityId, params?.search, params?.courierId],
		queryFn: () => fetchCourierOffices(privateAxios, params)
	});
}
export function useCourier(courierId: string) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["courier", courierId],
		queryFn: () => fetchCourier(privateAxios, courierId)
	});
}

export interface DistanceResponse {
	success: boolean;
	message: string;
	data: number;
}
export function useCalculateDistance() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["distance"],
		mutationFn: async (params: {
			pickupAddress: string;
			dropoffAddress: string;
		}): Promise<DistanceResponse> => {
			const { data } = (await privateAxios.get("/currier/calculate_distance", { params }));
			return data;
		}
	});
}

interface CalculateCourierFee {
	courierId: string;
	distance: number;
	weight: number;
	value: number;
}

interface CalculateCourierFeeResponse {
	totalFee: number;
	breakdown: {
		baseFee: number;
		distanceCharge: number;
		weightCharge: number;
		insuranceCharge: number;
	};
	input: {
		weight: number;
		distance: number;
		value: number;
	};
}
export function useCalculateCourierFee() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["courier-fee"],
		mutationFn: async (body: CalculateCourierFee): Promise<CalculateCourierFeeResponse> => {
			return (await privateAxios.post("/currier/calculate_courier_fee", body)).data.data;
		}
	});
}
export interface IntasidaFeeResponse {
	success: boolean;
	message: string;
	data: {
		amount: number;
		feeType: string;
		percentage: number;
		fee: number;
		total: number;
	};
}

export function useIntasidaFee() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["intasida-fee"],
		mutationFn: async (params: { amount: number; feeType: string }): Promise<IntasidaFeeResponse> => {
			const { data } = (await privateAxios.get("/banks/calculate-fee", { params }));
			return data.data;
		}
	});
}
